/**
 * React Query hooks for admin management.
 *
 * Provides hooks for user listing, detail, notes, analytics,
 * and mutation hooks for module grants, user enable/disable, role changes, and notes.
 * All use the shared api-client which auto-attaches Bearer tokens.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listUsers,
  getUserDetail,
  grantModule,
  revokeModule,
  disableUser,
  enableUser,
  deleteUser,
  changeRole,
  listNotes,
  createNote,
  getAnalytics,
} from './adminApi'
import { adminKeys } from './adminKeys'
import type { AdminActionResponse, ListUsersParams } from './types'

const THIRTY_SECONDS = 30 * 1000
const SIXTY_SECONDS = 60 * 1000

// Mutation variable types
interface ModuleMutationVars {
  userId: string
  moduleId: string
}

interface UserIdVars {
  userId: string
}

interface ChangeRoleVars {
  userId: string
  role: string
}

interface CreateNoteVars {
  userId: string
  note: string
}

/**
 * Fetch paginated admin user list.
 * staleTime: 30 seconds -- admin data may change frequently.
 */
export function useAdminUsers(params: ListUsersParams) {
  return useQuery({
    queryKey: adminKeys.userList(params),
    queryFn: () => listUsers(params),
    staleTime: THIRTY_SECONDS,
  })
}

/**
 * Fetch detailed info for a single user.
 * Enabled only when userId is provided.
 */
export function useAdminUserDetail(userId: string) {
  return useQuery({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () => getUserDetail(userId),
    enabled: !!userId,
  })
}

/**
 * Fetch admin notes for a user.
 * Enabled only when userId is provided.
 */
export function useAdminNotes(userId: string) {
  return useQuery({
    queryKey: adminKeys.userNotes(userId),
    queryFn: () => listNotes(userId),
    enabled: !!userId,
  })
}

/**
 * Fetch platform analytics.
 * staleTime: 60 seconds -- analytics are expensive and don't change rapidly.
 */
export function useAdminAnalytics() {
  return useQuery({
    queryKey: adminKeys.analytics(),
    queryFn: () => getAnalytics(),
    staleTime: SIXTY_SECONDS,
  })
}

// Phase 16 Plan 08: useAdminProducts / useAdminProduct / useCreateProduct /
// useUpdateProduct deleted. Replaced by /console/products' hooks at
// `@/core/console/controllers/queries/useProductsAdmin` and
// `@/core/console/controllers/mutations/useProductsMutations` which talk to
// /api/cms/admin/products (Plan 16-04) instead of the deleted legacy
// /api/admin/products surface.

/**
 * Grant a module to a user.
 * Invalidates user detail + user list on success.
 */
export function useGrantModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, moduleId }: ModuleMutationVars) =>
      grantModule(userId, moduleId),
    onSuccess: (
      _data: AdminActionResponse,
      variables: ModuleMutationVars
    ) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userDetail(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('Module granted')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to grant module'
      toast.error(message)
    },
  })
}

/**
 * Revoke a module from a user.
 * Invalidates user detail + user list on success.
 */
export function useRevokeModule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, moduleId }: ModuleMutationVars) =>
      revokeModule(userId, moduleId),
    onSuccess: (
      _data: AdminActionResponse,
      variables: ModuleMutationVars
    ) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userDetail(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('Module revoked')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to revoke module'
      toast.error(message)
    },
  })
}

/**
 * Disable a user account.
 * Invalidates user detail + user list on success.
 */
export function useDisableUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId }: UserIdVars) => disableUser(userId),
    onSuccess: (_data: AdminActionResponse, variables: UserIdVars) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userDetail(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('User disabled')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to disable user'
      toast.error(message)
    },
  })
}

/**
 * Enable a disabled user account.
 * Invalidates user detail + user list on success.
 */
export function useEnableUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId }: UserIdVars) => enableUser(userId),
    onSuccess: (_data: AdminActionResponse, variables: UserIdVars) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userDetail(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('User enabled')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to enable user'
      toast.error(message)
    },
  })
}

/**
 * Soft-delete (anonymize) a user account. Super-admin only.
 *
 * Invalidates the user LIST on success (the soft-deleted user is scrubbed and
 * filtered out server-side, so the list must refetch). We do NOT invalidate
 * userDetail — the page navigates away from the now-deleted record, so its
 * detail query is no longer mounted. Navigation is the page's responsibility:
 * pass an `onSuccess` to `mutate` (the same pattern useGrantModule uses for
 * `setSelectedModule`) so this hook stays navigation-free and reusable.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId }: UserIdVars) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('User deleted')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to delete user'
      toast.error(message)
    },
  })
}

/**
 * Change a user's role.
 * Invalidates user detail + user list on success.
 */
export function useChangeRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: ChangeRoleVars) =>
      changeRole(userId, role),
    onSuccess: (_data: AdminActionResponse, variables: ChangeRoleVars) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userDetail(variables.userId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      toast.success('Role updated')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to change role'
      toast.error(message)
    },
  })
}

/**
 * Create an admin note on a user.
 * Invalidates user notes on success.
 */
export function useCreateNote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, note }: CreateNoteVars) =>
      createNote(userId, note),
    onSuccess: (_data: AdminActionResponse, variables: CreateNoteVars) => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.userNotes(variables.userId),
      })
      toast.success('Note added')
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to create note'
      toast.error(message)
    },
  })
}
